import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { userService, UserSummary } from '../services/userService';
import './FollowersList.css';

const FollowersList: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [followers, setFollowers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFollowers = useCallback(async (token?: string) => {
    if (!username) return;

    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await userService.getFollowers(username, token);

      if (token) {
        setFollowers(prev => [...prev, ...response.followers]);
      } else {
        setFollowers(response.followers);
      }
      setNextToken(response.nextToken);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load followers';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [username]);

  useEffect(() => {
    loadFollowers();
  }, [loadFollowers]);

  const handleLoadMore = () => {
    if (nextToken && !loadingMore) {
      loadFollowers(nextToken);
    }
  };

  if (loading) {
    return (
      <div className="followers-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading followers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="followers-page">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => loadFollowers()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="followers-page">
      <header className="page-header">
        <Link to={`/users/${username}`} className="back-link">
          &larr; Back to profile
        </Link>
        <h1>Followers</h1>
        <p className="subtitle">@{username}'s followers</p>
      </header>

      {followers.length === 0 ? (
        <div className="empty-state">
          <p>No followers yet</p>
        </div>
      ) : (
        <>
          <div className="users-grid">
            {followers.map((user) => (
              <Link
                key={user.userId}
                to={`/users/${user.username}`}
                className="user-card"
              >
                <div className="user-avatar">
                  {user.profilePictureUrl ? (
                    <img src={user.profilePictureUrl} alt={user.username} />
                  ) : (
                    <div className="avatar-placeholder">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="user-info">
                  <h3 className="user-name">
                    {user.displayName || user.username}
                  </h3>
                  <p className="user-username">@{user.username}</p>
                </div>
              </Link>
            ))}
          </div>

          {nextToken && (
            <div className="load-more-container">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-secondary"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FollowersList;
